import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI Components
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  Typography,
  Grid2 as Grid
} from '@mui/material';

// MUI Icons
import {
  CloudUpload,
  CloudDownload,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Add as AddIcon,
  QrCode as QrCodeIcon
} from "@mui/icons-material";

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// Utilities & Helpers
import {
  addToSentItemsLS,
  calcRefFees,
  calcWfs,
  cleanItemMetadataToPreventQuotaExceeded,
  fetchNextDataWithRetry,
  fetchWrapper,
  fetchWrapperNoAuth,
  mergeObjects,
  sendErrorMessage,
  SERVER
} from '../../utils/helpers';
import {
  checkFeed,
  checkItem,
  convertItemWFS,
  MAX_STEP_RETRIES
} from '../../utils/proccessHelpers';

// Components
import Form from '../../components/ProcessComponents/Form';
import FormSF from '../../components/ProcessComponents/FormSF';
import CustomStepper from '../../components/ProcessComponents/CustomStepper';
import ResizableSidebar from '../../components/SideBar/ResizableSidebar';
import ResponsiveItemCard from '../../components/SideBar/ResponsiveItemCard';

// Modals
import BatchSelectionModal from '../../components/Modals/BatchSelectionModal';
import BarcodeLabelModal from '../../components/Modals/BarcodeLabelModal';
import ErrorModal from '../../components/Modals/ErrorModal';
import NewBatchModal from '../../components/Modals/NewBatchModal';
import QuestionModal from '../../components/Modals/QuestionModal';
import ShippmentsModal from '../../components/Modals/ShippmentsModal';

// Tables
import SearchedProductsTable from '../../components/Tables/SearchedProductsTable';

// Tooltips & Loaders
import HtmlTooltip from '../../components/Tooltip/HtmlTooltip';
import CustomCircularProgress from '../../components/Loaders/CustomCircularProgress';

// Inputs
import CustomInput from '../../components/Inputs/CustomInput';
import SelectComponent from '../../components/Inputs/SelectComponent';
import BarcodeMultipleLabelsModal from '../../components/Modals/BarcodeMultipleLabelsModal';
import SearchCatalogModal from '../../components/Modals/SearchCatalogModal';

// Local storage keys
const STORAGE_KEY_ADDED_ITEMS = 'IFAddItemProcess_addedItems';
const STORAGE_KEY_SEARCHED_ITEMS = 'IFAddItemProcess_searchedItems';
const STORAGE_KEY_BATCH_INFO = 'IFAddItemProcess_batchInfo';
const STORAGE_KEY_ACTIVE_STEP = 'IFAddItemProcess_activeStep';
const STORAGE_KEY_CURR_PRODUCT = 'IFAddItemProcess_currProduct';
const STORAGE_KEY = "IFAddItemProccess";

function AddItem(props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { encodedAuth, user, password, isSubUser } = useAuth();

  const [sidebarWidth, setSidebarWidth] = useState(320);

  const [successedItems, setSuccessedItems] = useState(0);
  const [barcodeLabelProduct, setBarcodeLabelProduct] = useState({});
  const [openMultipleBarcodeModal, setOpenMultipleBarcodeModal] = useState(false);
  const [openSearchCatalog, setOpenSearchCatalog] = useState(false);
  const [openBarcodeModal, setOpenBarcodeModal] = useState(false);
  const [shippmentOpenModal, setShippmentOpenModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openQuestionModal, setOpenQuestionModal] = useState(false);
  const [openDeleteFromBatchModal, setOpenDeleteFromBatchModal] = useState(false);
  const [itemToModify, setItemToModify] = useState({});
  const [openErrorModal, setopenErrorModal] = useState(false);
  const [loadingBarcode, setLoadingBarcode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [alertBatch, setAlertBatch] = useState({ open: false, severity: "info", message: "" });
  const [render, setRender] = useState(0);

  const [batchesArray, setBatchesArray] = useState([]);
  const [addedItems, setAddedItems] = useState([]);
  const [searchedItems, setSearchedItems] = useState([]);
  const [channel, setChannel] = useState('wfs');
  const [batchName, setBatchName] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [currProduct, setCurrProduct] = useState(null);
  const [loadStates, setLoadStates] = useState(false);

  const [newBatchOpen, setNewBatchOpen] = useState(batchName === "");
  const steps = ['Select Batch Name', 'Search item', 'Fill Product details'];

  const [prefetchStatus, setPrefetchStatus] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [identifierType, setIdentifierType] = useState("barcode");
  const [barcode, setBarcode] = useState(currProduct ? currProduct.upc : "");
  const [itemId, setItemId] = useState("");
  const [pName, setPName] = useState("");
  const [sku, setSku] = useState("");
  const [skuUpdate, setSkuUpdate] = useState(false);

  // Single useEffect to handle all localStorage updates
  useEffect(() => {
    if (batchName === "") {
      return;
    }
    if (loadStates && addedItems.length > 0) {
      const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      storage[batchName] = {
        addedItems: addedItems.map(cleanItemMetadataToPreventQuotaExceeded),
        searchedItems: searchedItems,
        channel: channel,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    }
  }, [loadStates, addedItems, searchedItems, channel]);

  const loadStatesFromLocalStorage = (batchSelected) => {
    try {
      const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      setAddedItems((storage[batchSelected].addedItems || []).map(cleanItemMetadataToPreventQuotaExceeded));
      setSuccessedItems((storage[batchSelected].addedItems || []).filter(item => item.status === "SUCCESS").length);
      setSearchedItems(storage[batchSelected].searchedItems || []);
      setChannel(storage[batchSelected].channel || "wfs");
      setBatchName(batchSelected || "");
      setActiveStep(1);
      setLoadStates(true);
      setOpenConfirmModal(false);
    } catch (error) {
      console.error("Failed to load states from localStorage", error);
      // Ensure default values if an error occurs
      setAddedItems([]);
      setSuccessedItems(0);
      setSearchedItems([]);
      setChannel("wfs");
      setBatchName("");
      setActiveStep(0);
      setLoadStates(true);
      setOpenConfirmModal(false);
    }
  };

  // This useEffect runs once on component mount to load from localStorage
  useEffect(() => {
    const loadPrevData = async () => {
      try {
        const oldStorage = {
          addedItems: JSON.parse(localStorage.getItem(STORAGE_KEY_ADDED_ITEMS)) || [],
          searchedItems: JSON.parse(localStorage.getItem(STORAGE_KEY_SEARCHED_ITEMS)) || [],
          batchInfo: JSON.parse(localStorage.getItem(STORAGE_KEY_BATCH_INFO)) || {},
        };
        if ((oldStorage.addedItems.length > 0 || oldStorage.searchedItems.length > 0) && oldStorage.batchInfo?.batchName) {
          const newStorage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
          newStorage[oldStorage.batchInfo?.batchName] = {
            addedItems: oldStorage.addedItems,
            searchedItems: oldStorage.searchedItems,
            channel: oldStorage.channel
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorage));
          localStorage.removeItem(STORAGE_KEY_ADDED_ITEMS);
          localStorage.removeItem(STORAGE_KEY_SEARCHED_ITEMS);
          localStorage.removeItem(STORAGE_KEY_BATCH_INFO);
          localStorage.removeItem(STORAGE_KEY_ACTIVE_STEP);
          localStorage.removeItem(STORAGE_KEY_CURR_PRODUCT);
        }
      } catch (error) {
        console.error("Error checking localStorage", error);
      }
    }
    loadPrevData();

    const loadInitialState = async () => {
      try {
        const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        const batches = Object.keys(storage);
        if (batches.length > 0) {
          setBatchesArray(batches);
          setOpenConfirmModal(true);
        } else {
          setLoadStates(true); // No stored data, enable storing right away
        }

        localStorage.removeItem(STORAGE_KEY_ADDED_ITEMS);
        localStorage.removeItem(STORAGE_KEY_SEARCHED_ITEMS);
        localStorage.removeItem(STORAGE_KEY_BATCH_INFO);
        localStorage.removeItem(STORAGE_KEY_ACTIVE_STEP);
        localStorage.removeItem(STORAGE_KEY_CURR_PRODUCT);
      } catch (error) {
        console.error("Error checking localStorage", error);
        setLoadStates(true);
      }
    };

    loadInitialState();
  }, []);

  // Clear localStorage data when items are successfully sent
  const clearStoredData = (batchSelected) => {
    const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    delete storage[batchSelected];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    setBatchesArray((batches) => batches.filter(b => b !== batchSelected));
  };

  const showAlert = (severity, message) => {
    setAlertBatch({ open: true, severity, message });

    // Auto-close after 3 seconds
    setTimeout(() => {
      setAlertBatch({ ...alertBatch, open: false });
    }, 3000);
  };

  const handleSaveBatches = async () => {
    const batches = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    if (Object.keys(batches).length > 0) {
      const body = {
        email: user,
        page: "AddItem",
        batches: batches
      };
      try {
        await fetchWrapperNoAuth(`${SERVER}/falcon_save_batches`, user, password, { method: "POST", body: body });
        showAlert("success", "Batches have been successfully saved!");
      } catch (e) {
        showAlert("error", "An error occurred while saving batches. Please try again.");
      }
    } else {
      showAlert("warning", "No batches found to save. Please add some batches first.");
    }
  };

  const handleLoadBatches = async () => {
    const body = {
      email: user,
      page: "AddItem"
    };
    try {
      const batches = await fetchWrapperNoAuth(`${SERVER}/falcon_load_batches`, user, password, { method: "POST", body: body });
      if (Object.keys(batches).length > 0) {
        setAddedItems([]);
        setSuccessedItems(0);
        setSearchedItems([]);
        setChannel("wfs");
        setBatchName("");
        setActiveStep(1);
        showAlert("success", "Batches have been successfully loaded! Refresh the page to view them.");
        const cleanedBatches = {};
        Object.keys(batches).forEach(batchName => {
          cleanedBatches[batchName] = {
            ...batches[batchName],
            addedItems: (batches[batchName].addedItems || []).map(cleanItemMetadataToPreventQuotaExceeded),
            searchedItems: (batches[batchName].searchedItems || []),
            channel: batches[batchName].channel
          };
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedBatches));
        window.location.reload();
      } else {
        showAlert("info", "No saved batches found in the cloud.");
      }
    } catch (e) {
      console.error(e);
      showAlert("error", "An error occurred while loading batches. Please try again.");
    }
  };

  const alertError = (msg) => {
    setErrorMessage(msg);
    setopenErrorModal(true);
  }

  const prefetchItemData = async (item) => {
    if (!item || prefetchStatus[item.itemId]) return;

    setPrefetchStatus(prev => ({
      ...prev,
      [item.itemId]: 'loading'
    }));

    try {
      let nextData = await fetchNextDataWithRetry(user, password, item.itemId);
      if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
        try {
          const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${item.upc || nextData.upc}`, user, password);
          if (azData.fromAz) {
            if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
              nextData.dimsFromAz = true;
              nextData.length = azData.length;
              nextData.height = azData.height;
              nextData.width = azData.width;
            } else {
              nextData.dimsFromAz = false;
            }
            if (!nextData.weight && azData.weight) {
              nextData.weightFromAz = true;
              nextData.weight = azData.weight;
            } else {
              nextData.weightFromAz = false;
            }
            if (!nextData.manufacturer && azData.manufacturer) {
              nextData.manufacturerFromAz = true;
              nextData.manufacturer = azData.manufacturer;
            } else {
              nextData.manufacturerFromAz = false;
            }
            if (!nextData.brand && azData.brand) {
              nextData.brandFromAz = true;
              nextData.brand = azData.brand;
            } else {
              nextData.brandFromAz = false;
            }
          }
        } catch (error) {
          console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${item.upc}:`, error.message);
        }
      }

      const updatedItem = mergeObjects(item, nextData);

      setSearchedItems(prevItems =>
        prevItems.map(prevItem =>
          prevItem.itemId === item.itemId
            ? updatedItem
            : prevItem
        )
      );

      // Update currProduct if this is the currently selected item
      setCurrProduct(prevProduct =>
        prevProduct?.itemId === item.itemId
          ? updatedItem
          : prevProduct
      );

      setPrefetchStatus(prev => ({
        ...prev,
        [item.itemId]: 'completed'
      }));
    } catch (error) {
      console.error(`Failed to prefetch data for Item Id: ${item.itemId}:`, error.message);
      setPrefetchStatus(prev => ({
        ...prev,
        [item.itemId]: 'error'
      }));
    }
  };

  function isNumeric(str) {
    return /^[0-9]+$/.test(str);
  }

  const handleSearch = async (productId) => {
    if (!productId) {
      alertError("Enter Valid Value to see results");
      setSearchedItems([]);
      return;
    }
    setSearchLoading(true);
    let uniqeItems = [];
    let tempIdentifierTypeCheck = identifierType;
    if (identifierType === "barcode") {
      if (productId.length < 11) {
        tempIdentifierTypeCheck = "itemId";
        setBarcode("");
        setItemId(productId);
      }
      if (!isNumeric(productId)) {
        tempIdentifierTypeCheck = "pName";
        setBarcode("");
        setPName(productId);
      }
    }
    setIdentifierType(tempIdentifierTypeCheck);

    let tempIdentifierType = "upc";
    if (tempIdentifierTypeCheck === "itemId" || tempIdentifierTypeCheck === "pName") {
      tempIdentifierType = "query";
    } else if (tempIdentifierTypeCheck === "barcode") {
      try {
        if (productId.length < 12) {
          productId = productId.padStart(12, '0');
        }
        const barcodePrds = await fetchWrapperNoAuth(`${SERVER}/get_options_from_barcode/${productId}`, user, password);
        uniqeItems = barcodePrds
      } catch (error) {
        console.error('Error:', error.message);
        setSearchedItems([]);
      }
      tempIdentifierType = productId.length === 12 ? "upc" : "gtin";
    } else {
      try {
        const itemData = await fetchWrapper(`${SERVER}/post_catalog_search_sku/${productId}`, user, password, encodedAuth);
        setSearchedItems([itemData]);
        setCurrProduct(itemData);
        setSearchLoading(false);
        // Start prefetching for the single item
        prefetchItemData(itemData);
        return;
      } catch (error) {
        console.error('Error:', error);
        setSearchedItems([]);
        setSearchLoading(false);
        return;
      }
    }

    const url = `${SERVER}/get_item_search`;
    const body = {
      [tempIdentifierType]: productId
    };

    try {
      const items = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
      const concatItems = [...uniqeItems, ...items].reduce((acc, current) => {
        if (!acc.some(item => item.itemId === current.itemId)) {
          acc.push(current);
        }
        return acc;
      }, []);
      setSearchedItems(concatItems);
      setCurrProduct(concatItems[0]);
      setSearchLoading(false);

      // Start prefetching for all items
      concatItems.slice(0, 5).forEach(item => prefetchItemData(item));
    } catch (error) {
      console.error('Error:', error);
      setSearchedItems(uniqeItems);
      setSearchLoading(false);
      uniqeItems.slice(0, 5).forEach(item => prefetchItemData(item));
    }
  };

  const resetStates = () => {
    setIdentifierType("barcode");
    setBarcode("");
    setItemId("");
    setPName("");
    setSku("");
    setSearchedItems([]);
    setCurrProduct({});
    setPrefetchStatus({});
  }

  const handleAddItem = (product) => {
    if (!product) return;

    setAddedItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => item.sku === product.sku);

      if (existingIndex !== -1) {
        // Replace the existing item
        return prevItems.map((item, index) =>
          index === existingIndex ? { ...product } : item
        );
      }

      // Add new product to the beginning
      return [{ ...product }, ...prevItems];
    });

    resetStates();
    setActiveStep(1);
  };

  const handleAddAnotherItem = () => {
    setActiveStep(1);
    resetStates();
  };

  const handleSendAllItems = () => {
    if (addedItems.length === 0) {
      alertError('First add items');
      return;
    }
    if (addedItems.every(item => item.status !== "LOADING")) {
      if (channel === "sf") {
        alertError('We have added all the items to your catalog as SF. Now, you need to go there and set the desired quantities(inventory). Next week, we will automate this as well! :)');
        // const items = addedItems.filter(item => item.status === "SUCCESS").map((item) => {
        //   return {
        //     sku: item.sku,
        //     quantity: {
        //       amount: item.qty,
        //       unit: "EACH"
        //     }
        //   }
        // });
        // const result = await addItemsToInventory(items, user, password, encodedAuth)
      } else {
        setShippmentOpenModal(true);
      }
    } else {
      alertError('Please wait until all items have finished processing!');
    }
  };

  const removeFromBatch = async (item) => {
    // Remove from addedItems array
    setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== item.sku)));

    // Remove batchName from DB
    const metadata = {
      batchName: "none",
      email: user,
      productName: item.productName,
      image: item.image,
      sku: item.sku,
      gtin: item.gtin,
      upc: item.upc,
      cost: item.cost,
      qty: item.qty,
      innerPack: item.innerPack,
      source: item.source || "",
      note: item.note || "",
      expirationDate: item.expiration_date || null,
      inboundOrderId: item.inbound_order_id || "",
      shipmentId: item.shipment_id || "",
      purchaseDate: item.purchase_date || "",
      productType: item.product_type,
      schema: item.schema,
      MPItem: item.mp_item
    }
    const body = {
      sku: item.sku,
      email: user,
      batchName: "none",
      metadata: metadata
    }
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_batch`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  const editItem = (item) => {
    setCurrProduct(item);
    setBarcode(item.upc);
    setActiveStep(2);
  };

  const retryAddItem = async (item) => {
    const url = `${SERVER}/send_item_feed_match`;
    const body = {
      item: {
        sku: item.sku,
        upc: item.upc,
        price: item.price.amount,
        weight: item.weight
      }
    }
    try {
      const { feedId } = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
      // Create a new array by mapping through the current items
      const updatedItems = addedItems.map(existingItem => {
        // If the SKU matches, replace the item with the new one
        if (existingItem.sku === item.sku) {
          return {
            ...item,
            step: 1,
            feedId: feedId,
            status: "LOADING", // Reset the status
            retryCount: 0, // Increment retry count
            stepRetryCount: 0, // Increment step retry count
            lastRequest: new Date().toISOString() // Update the last request timestamp
          };
        }
        // Otherwise keep the existing item
        return existingItem;
      });

      // Update the state with the new array
      setAddedItems(updatedItems);
    } catch (error) { }
  };

  // Steps
  const handleNext = async (product) => {
    if (activeStep === 0) {
      if (!batchName) {
        alert("Create new Batch");
        setNewBatchOpen(true);
        return;
      }
    }

    if (activeStep === 1) {
      if (!product || Object.keys(product).length === 0) {
        alertError("Please select item")
        return;
      }
      // if (!product.isMarketPlaceItem) {
      //   alertError(`Item ${product.itemId} is not marketplace item.`);
      //   return;
      // }
      setScrapeLoading(true);
      let tempProduct = product;

      // If nextData wasn't successfully prefetched, fetch it now
      if (prefetchStatus[product.itemId] !== 'completed' || !product.hasOwnProperty("length")) {
        try {
          let nextData = await fetchNextDataWithRetry(user, password, product.itemId);
          if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
            try {
              const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${product.upc || nextData.upc}`, user, password);
              if (azData.fromAz) {
                if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
                  nextData.dimsFromAz = true;
                  nextData.length = azData.length;
                  nextData.height = azData.height;
                  nextData.width = azData.width;
                } else {
                  nextData.dimsFromAz = false;
                }
                if (!nextData.weight && azData.weight) {
                  nextData.weightFromAz = true;
                  nextData.weight = azData.weight;
                } else {
                  nextData.weightFromAz = false;
                }
                if (!nextData.manufacturer && azData.manufacturer) {
                  nextData.manufacturerFromAz = true;
                  nextData.manufacturer = azData.manufacturer;
                } else {
                  nextData.manufacturerFromAz = false;
                }
                if (!nextData.brand && azData.brand) {
                  nextData.brandFromAz = true;
                  nextData.brand = azData.brand;
                } else {
                  nextData.brandFromAz = false;
                }
              }
            } catch (error) {
              console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${product.upc || nextData.upc}:`, error.message);
            }
          }
          tempProduct = mergeObjects(tempProduct, nextData);

          if (nextData.upc) {
            setBarcode(nextData.upc);
          }
        } catch (error) {
          console.error(`Failed to fetch from get_item_nextdata for Item Id: ${product.itemId}:`, error.message);
        }
      } else {
        // If nextData was prefetched, get the updated product from searchedItems
        tempProduct = searchedItems.find(item => item.itemId === product.itemId);
        if (tempProduct.upc) {
          setBarcode(tempProduct.upc);
        }
      }

      // Always fetch catalog search data when moving to next step
      let foundOnCatalog = false;
      try {
        const catalogSearch = await fetchWrapper(`${SERVER}/post_catalog_search/${product.itemId}/${user}`, user, password, encodedAuth);
        foundOnCatalog = true;
        tempProduct = mergeObjects(tempProduct, catalogSearch.dbItems[0] || {});
        tempProduct = mergeObjects(tempProduct, catalogSearch.ctItems[0] || {});

        if (catalogSearch.upc) {
          setBarcode(catalogSearch.upc);
        }
      } catch (error) {
        console.error(`Failed to fetch from post_catalog_search for Item Id: ${product.itemId}:`, error.message);
      }

      setCurrProduct(tempProduct);
      // if (foundOnCatalog) {
      //   console.log(tempProduct);
      //   setOpenQuestionModal(true);
      //   setScrapeLoading(false);
      //   return;
      // }
      setScrapeLoading(false);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <NewBatchModal
            open={newBatchOpen} setOpen={setNewBatchOpen}
            batchName={batchName} setBatchName={setBatchName}
            channel={channel} setChannel={setChannel}
            handleNext={handleNext}
          />
        </Box>
      )
    } else if (activeStep === 1) {
      return (
        <>
          {scrapeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', }}>
              <CustomCircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ margin: 'auto', alignItems: 'center', px: { xs: 1, sm: 2 } }}>
                <Grid item size={{ xs: 12, sm: 6, md: 3, lg: 2 }} offset={{ md: 1, lg: 2 }}>
                  <SelectComponent
                    value={identifierType}
                    title={"Identifier Type"}
                    onChange={setIdentifierType}
                    options={[
                      { value: "barcode", name: "Barcode" },
                      { value: "pName", name: "Product Name" },
                      { value: "itemId", name: "Item ID" },
                      { value: "sku", name: "SKU" }
                    ]}
                    width={"fullWidth"}
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                  {identifierType === "barcode" && (
                    <CustomInput
                      fullWidth
                      autoFocus
                      label={"Barcode"}
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      onPaste={(e) => {
                        setTimeout(() => {
                          const pastedValue = e.target.value;
                          setBarcode(pastedValue);
                          handleSearch(pastedValue.trim());
                        }, 0);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(barcode.trim()); } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{
                              color: barcode ? theme.text.primary : theme.accent.error,
                              fontFamily: theme.fontFamily
                            }}>
                              #
                            </Typography>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleSearch(barcode.trim())}
                              edge="end"
                              sx={{ color: barcode ? theme.text.primary : theme.accent.error }}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      isError={!barcode || barcode === ""}
                    />
                  )}

                  {identifierType === "itemId" && (
                    <CustomInput
                      fullWidth
                      label={"Item Id"}
                      value={itemId}
                      onChange={(e) => setItemId(e.target.value)}
                      onPaste={(e) => {
                        setTimeout(() => {
                          const pastedValue = e.target.value;
                          setItemId(pastedValue);
                          handleSearch(pastedValue.trim());
                        }, 0);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(itemId.trim()); } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleSearch(itemId.trim())}
                              edge="end"
                              sx={{ color: itemId ? theme.text.primary : theme.accent.error }}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      isError={!itemId || itemId === ""}
                    />
                  )}

                  {identifierType === "pName" && (
                    <CustomInput
                      fullWidth
                      label={"Product Name"}
                      value={pName}
                      onChange={(e) => setPName(e.target.value)}
                      onPaste={(e) => {
                        setTimeout(() => {
                          const pastedValue = e.target.value;
                          setPName(pastedValue);
                          handleSearch(pastedValue.trim());
                        }, 0);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(pName.trim()); } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleSearch(pName.trim())}
                              edge="end"
                              sx={{ color: pName ? theme.text.primary : theme.accent.error }}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      isError={!pName || pName === ""}
                    />
                  )}

                  {identifierType === "sku" && (
                    <CustomInput
                      fullWidth
                      label={"SKU"}
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      onPaste={(e) => {
                        setTimeout(() => {
                          const pastedValue = e.target.value;
                          setSku(pastedValue);
                          handleSearch(pastedValue.trim());
                        }, 0);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(sku.trim()); } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleSearch(sku.trim())}
                              edge="end"
                              sx={{ color: sku ? theme.text.primary : theme.accent.error }}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      isError={!sku || sku === ""}
                    />
                  )}
                </Grid>

                <Grid item size={{ xs: 12, md: 4, lg: 3 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    fullWidth
                    sx={{
                      py: { xs: 1, sm: 0.5 },
                      fontFamily: theme.fontFamily,
                      backgroundColor: theme.color.primary,
                      borderRadius: '20px',
                      textTransform: "none",
                      fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.875rem' },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        color: theme.text.negative,
                        backgroundColor: theme.accent.disabled,
                      }
                    }}
                    onClick={() => setOpenSearchCatalog(true)}
                  >
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none', md: 'inline' } }}>
                      Add item from catalog to batch
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline', md: 'none' } }}>
                      Add from catalog
                    </Box>
                  </Button>
                </Grid>
              </Grid>

              <SearchedProductsTable products={searchedItems} setCurrProduct={setCurrProduct} isLoading={searchLoading} handleNext={handleNext} />
            </>
          )}

        </>
      )
    } else if (activeStep === 2) {
      return (
        <>
          {channel === "wfs" ? (
            <Form currProduct={currProduct} barcode={barcode} handleAddItem={handleAddItem} batchName={batchName} skuUpdate={skuUpdate} setSkuUpdate={setSkuUpdate} />
          ) : (
            <FormSF currProduct={currProduct} barcode={barcode} handleAddItem={handleAddItem} batchName={batchName} />
          )}
        </>
      )
    }
  };

  const changeQty = async (value, item) => {
    if (value === "") {
      return;
    }
    try {
      const body = {
        sku: item.sku,
        email: user,
        qty: parseInt(value)
      }
      item.qty = parseInt(value);
      setRender(p => p + 1);
      fetchWrapperNoAuth(`${SERVER}/falcon_update_qty`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  // Items Proccess Pipeline
  const processItems = async () => {  // filter to get only LOADING items
    const loadingItems = addedItems.filter((item) => ["LOADING", "TIME_BRAKE"].includes(item.status));
    const restItems = addedItems.filter((item) => !["LOADING", "TIME_BRAKE"].includes(item.status));

    const updatedLoadingItems = await Promise.all(
      loadingItems.map(async (item) => {
        try {
          let updatedItem;
          if ((item.isWfs || channel === "wfs") && !item.skuUpdate) {
            if (item.step === 1) {          // Check feed status (needs to be PROCESSED / RECEIVED)
              updatedItem = await checkFeed(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 2) {   // Check published status (needs to be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS)
              updatedItem = await checkItem(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 3) {   // Convert item to WFS (using the item.scheme) and refresh the item.feedId
              updatedItem = await convertItemWFS(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 4) {   // Check feed status (needs to be PROCESSED / RECEIVED) after convert to WFS
              updatedItem = await checkFeed(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 5) {   // Check published status (needs to be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS) after convert to WFS
              updatedItem = await checkItem(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 6) {
              updatedItem = {
                ...item,
                status: (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR") ? "SUCCESS" : "ERROR",
                stepRetryCount: 0 // Reset for future use
              };
              if (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR" && isSubUser) {
                addToSentItemsLS(user, item);
              }
            } else {
              updatedItem = item;
            }
          } else {
            if (item.step === 1) {          // Check feed status (needs to be PROCESSED / RECEIVED)
              updatedItem = await checkFeed(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 2) {   // Check published status (needs to be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS)
              updatedItem = await checkItem(item, user, password, channel, "AddItem.js", encodedAuth);
            } else if (item.step === 3) {
              updatedItem = {
                ...item,
                status: (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR") ? "SUCCESS" : "ERROR",
                step: 6,
                stepRetryCount: 0 // Reset for future use
              };
              if (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR" && isSubUser) {
                addToSentItemsLS(user, item);
              }
            } else {
              updatedItem = item;
            }
          }

          return updatedItem;
        } catch (error) {
          // If anything fails, check if we should retry this step
          console.error(error);

          const stepRetryCount = (item.stepRetryCount || 0) + 1;

          // If we haven't reached max retries for this step, don't send error message yet
          if (stepRetryCount < MAX_STEP_RETRIES) {
            console.log(`Retrying step ${item.step} for item ${item.sku} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);
            return {
              ...item,
              stepRetryCount,
              lastRequest: new Date().toISOString()
            };
          }

          // If we've reached max retries, send error message and update status
          sendErrorMessage(user, password, channel, item.itemId, item.upc, "AddItem.js", item.step, "processItems", "ERROR", [error]);
          return {
            ...item,
            status: 'ERROR',
            lastRequest: new Date().toISOString(),
            stepRetryCount: 0 // Reset for future use
          };
        }
      })
    );

    if (updatedLoadingItems.filter(item => item.status === "SUCCESS").length > successedItems) {
      handleSaveBatches();
    }

    setSuccessedItems(updatedLoadingItems.filter(item => item.status === "SUCCESS").length);
    setAddedItems([...restItems, ...updatedLoadingItems]);
  };

  const handleAddItemReplen = async (product) => {
    handleAddItem({ ...product, status: "UPLOAD", step: 7 });
    const body = { sku: product.sku };
    const url = `${SERVER}/get_an_item`;
    try {
      const response = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body });
      const itemData = response.ItemResponse[0];
      if (itemData.publishedStatus === "PUBLISHED") {
        const addedItem = {
          ...product,
          availability: itemData.availability,
          condition: itemData.condition,
          gtin: itemData.gtin,
          lifecycleStatus: itemData.lifecycleStatus,
          mart: itemData.mart,
          price: itemData.price,
          productName: itemData.productName,
          title: itemData.productName,
          productType: itemData.productType,
          publishedStatus: itemData.publishedStatus,
          shelf: JSON.stringify(itemData.shelf.split("/")),
          sku: itemData.sku,
          unpublishedReasons: itemData.unpublishedReasons ?? { reason: [] },
          upc: itemData.upc,
          wpid: itemData.wpid,

          status: "SUCCESS",
          feedStatus: "PROCESSED",
          feedId: '',

          images: [],
          MPItem: product.mp_item,
          isWfs: true,
          lastRequest: new Date().toISOString(),
          step: 6
        }
        handleAddItem(addedItem);
      } else {
        setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== product.sku)));
        alertError(`Product with sku : ${product.sku} is not published!`);
        return;
      }
    } catch (e) {
      console.error(e);
      setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== product.sku)));
      return;
    }
  };

  useEffect(() => {
    const interval = setInterval(processItems, 10000);
    return () => clearInterval(interval);
  }, [addedItems]);

  useEffect(() => {
    if (addedItems.length > 0) {
      processItems();
    }
  }, [addedItems.length]);

  const styles = {
    root: {
      display: 'flex',
      height: '100%',
      bgcolor: theme.background.primary,
    },
    mainContent: {
      flex: 1,
      p: 3,
      overflow: 'auto'
    },
    itemsList: {
      flex: 1,
      overflowY: 'auto',
      marginRight: '5px',
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
        height: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '15px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    buttonContainer: {
      p: 2,
      borderTop: `1px solid ${theme.border.medium}`,
      bgcolor: theme.background.secondary,
    },
    navigationButton: {
      width: '100%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
      backgroundColor: theme.color.primary,
    }
  };

  if (!encodedAuth || encodedAuth === "") {
    alert("Please enter you credentials (Client Id & Client Secret from Walmart)");
    navigate('/account');
  }

  return (
    <Box sx={styles.root}>
      {/* Main Content Area */}
      <Box sx={styles.mainContent}>
        <Typography variant="h5" sx={{ textAlign: 'center', mb: 3, fontFamily: theme.fontFamily, color: theme.text.primary }}>
          Add an Item
        </Typography>

        {/* Stepper */}
        <CustomStepper activeStep={activeStep} steps={steps} />

        {/* Main form content */}
        <Box sx={{ mt: 2, }}>
          {getStep()}
        </Box>
      </Box>

      {/* Sidebar */}
      <ResizableSidebar width={sidebarWidth} setWidth={setSidebarWidth}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.border.primary}`, color: theme.text.primary, }}>
          <Typography variant="h6" sx={{ cursor: 'pointer', fontFamily: theme.fontFamily }} onClick={() => { if (activeStep === 0) setNewBatchOpen(true) }}>
            {batchName !== "none" && batchName}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily }}>
              Added Items ({addedItems.length})
            </Typography>
            <Box sx={{ display: 'flex' }}>
              {addedItems.length > 0 && <>
                {loadingBarcode ? <>
                  <CustomCircularProgress />
                </> : <>
                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                        Click to download Barcode labels
                      </Typography>
                    }
                    arrow
                    placement={batchName !== "" ? "top" : "bottom"}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -15],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <IconButton
                      onClick={() => setOpenMultipleBarcodeModal(true)}
                      sx={{ color: theme.text.primary, }}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </HtmlTooltip>
                </>}
              </>}
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Save batches to the cloud
                  </Typography>
                }
                arrow
                placement={batchName !== "" ? "top" : "bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  onClick={handleSaveBatches}
                  sx={{ color: theme.accent.success, }}
                >
                  <CloudUpload />
                </IconButton>
              </HtmlTooltip>
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Load batches from the cloud
                  </Typography>
                }
                arrow
                placement={batchName !== "" ? "top" : "bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  onClick={handleLoadBatches}
                  sx={{ color: theme.color.primary, }}
                >
                  <CloudDownload />
                </IconButton>
              </HtmlTooltip>
            </Box>
          </Box>
        </Box>

        {/* Added Items List */}
        <Box sx={styles.itemsList}>
          {addedItems.map((item, index) => (
            <ResponsiveItemCard
              key={index}
              item={item}
              index={index}
              theme={theme}
              onRetry={retryAddItem}
              changeQty={changeQty}
              editItem={editItem}
              setItemToModify={setItemToModify}
              setOpenDeleteFromBatchModal={setOpenDeleteFromBatchModal}
              setBarcodeLabelProduct={setBarcodeLabelProduct}
              setOpenBarcodeModal={setOpenBarcodeModal}
              sidebarWidth={sidebarWidth}
              isWfs={channel === "wfs"}
            />
          ))}
        </Box>

        <Box sx={{ ml: '10px', mt: '10px' }} render={render}>
          {(() => {
            const itemsToSum = addedItems.filter(item => item.publishedStatus === "PUBLISHED")
            const sellPrice = itemsToSum.reduce((sum, item) => sum + ((item.price?.amount ?? 0) * (item.qty || 1)), 0);
            const costPrice = itemsToSum.reduce((sum, item) => sum + ((item.cost || 0) * (item.qty || 1)), 0);
            const totalRefferalFees = itemsToSum.reduce((sum, item) => sum + ((calcRefFees(item)) * (item.qty || 1)), 0);
            const totalWfsCost = itemsToSum.reduce((sum, item) => sum + ((calcWfs(item) + (((item.price?.amount ?? 0) && (item.price?.amount ?? 0) < 10) ? 1 : 0)) * (item.qty || 1)), 0);
            const feesPrice = totalRefferalFees + totalWfsCost;
            const profit = sellPrice - costPrice - feesPrice;
            const totalUnits = itemsToSum.reduce((sum, item) => sum + (item.qty || 1), 0);

            return (
              <>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total units: {totalUnits}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total sell price: ${sellPrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total cost price: ${costPrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: profit > 0 ? theme.accent.success : theme.accent.error }}>
                  Profit: {profit < 0 && "-"}${profit < 0 ? (profit * -1).toFixed(2) : profit.toFixed(2)}
                </Typography>
              </>
            )
          })()}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={styles.buttonContainer}>
          {activeStep === 0 && !batchName && (
            <Button
              variant="contained"
              sx={{
                ...styles.navigationButton,
                backgroundColor: theme.accent.success,
              }}
              onClick={() => setNewBatchOpen(true)}
            >
              Create New Batch
            </Button>
          )}
          <Button
            variant="outlined"
            sx={{
              ...styles.navigationButton,
              color: theme.color.primary,
              backgroundColor: theme.background.primary,
              "&.Mui-disabled": {
                color: 'gray',
              },
            }}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            disabled={activeStep === 0}
          >
            {activeStep === 1 ? "Change Batch Name" : "Previous Step"}
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={handleAddAnotherItem}
              startIcon={<AddIcon />}
            >
              Add Another Item
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={() => handleNext(currProduct)}
              endIcon={<ArrowForwardIcon />}
            >
              Next Step
            </Button>
          )}
          <Button
            variant="contained"
            sx={styles.navigationButton}
            onClick={handleSendAllItems}
            startIcon={<SendIcon />}
          >
            {channel === "sf" ? "Add" : "Send"} All Items ({addedItems.filter(item => item.status === "SUCCESS").length})
          </Button>
        </Box>
      </ResizableSidebar>

      <Snackbar
        open={alertBatch.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}  // Position at the top-center
        autoHideDuration={3000}
        onClose={() => setAlertBatch({ ...alertBatch, open: false })}
        sx={{ marginTop: props.navbarHeight }}  // Add margin of 10vh from the top
      >
        <Alert severity={alertBatch.severity} sx={{ width: "100%", zIndex: 999, fontFamily: theme.fontFamily }}>
          {alertBatch.message}
        </Alert>
      </Snackbar>

      <QuestionModal
        open={openQuestionModal}
        setOpen={setOpenQuestionModal}
        message={"You have this item in catalog. want to change quantity?"}
        confirm={() => { alert('change qty'); setOpenQuestionModal(false); }}
        cancel={() => { setActiveStep((prevActiveStep) => prevActiveStep + 1); setOpenQuestionModal(false); }}
      />

      <QuestionModal
        open={openDeleteFromBatchModal}
        setOpen={setOpenDeleteFromBatchModal}
        message={
          <Box>
            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '16px', }}>This item will be removed only from your batch, not from your inventory.</Typography>
            <br />
            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '16px', fontWeight: 'bold', }}>Are you sure you want to proceed?</Typography>
          </Box>
        }
        confirm={() => { removeFromBatch(itemToModify); setOpenDeleteFromBatchModal(false); }}
        cancel={() => setOpenDeleteFromBatchModal(false)}
      />

      <BatchSelectionModal
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        batchesArray={batchesArray}
        handleSelectBatch={(batchSelected) => loadStatesFromLocalStorage(batchSelected)}
        removeBatch={(batchSelected) => clearStoredData(batchSelected)}
        cancel={() => { setLoadStates(true); setOpenConfirmModal(false); }}
      />

      <BarcodeLabelModal open={openBarcodeModal} setOpen={setOpenBarcodeModal} product={barcodeLabelProduct} />
      <BarcodeMultipleLabelsModal open={openMultipleBarcodeModal} setOpen={setOpenMultipleBarcodeModal} items={addedItems} setLoading={setLoadingBarcode} />

      <ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />

      <ShippmentsModal open={shippmentOpenModal} setOpen={setShippmentOpenModal} items={addedItems.filter(item => item.status === "SUCCESS")} clearStoredData={() => clearStoredData(batchName)} />

      <SearchCatalogModal
        open={openSearchCatalog}
        onClose={() => setOpenSearchCatalog(false)}
        setCurrProduct={setCurrProduct}
        handleNext={handleAddItemReplen}
        isReplen={true}
      />

    </Box>
  );
}

export default AddItem;
